import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { 
  FolderTree, 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  Layers, 
  Clock, 
  IndianRupee, 
  Percent, 
  CheckCircle2, 
  XCircle, 
  Upload, 
  Image as ImageIcon, 
  Loader2, 
  X,
  ChevronRight,
  ChevronDown,
  Filter,
  AlertCircle,
  HelpCircle,
  FolderPlus,
  Sparkles
} from 'lucide-react';
import { ServiceTemplateActivationDrawer } from './ServiceTemplateActivationDrawer';

interface ServiceCategory {
  id: string;
  tenant_id: string;
  branch_id?: string | null;
  category_name: string;
  parent_category_id?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface ServiceCatalogItem {
  id: string;
  tenant_id: string;
  branch_id?: string | null;
  category_id: string;
  service_name: string;
  description?: string | null;
  unit_id?: string | null;
  base_price: number;
  duration_minutes?: number | null;
  allow_custom_price: boolean;
  hsn_sac_id?: string | null;
  gst_rate: number;
  photo?: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface UnitItem {
  id: string;
  unit_name: string;
  unit_symbol?: string | null;
  unit_type?: string | null;
}

interface HsnSacItem {
  id: string;
  code: string;
  code_type?: string | null;
  description?: string | null;
  gst_rate_percent?: number | null;
}

interface ServiceCatalogViewProps {
  branchId?: string | null;
}

export function ServiceCatalogView({ branchId }: ServiceCatalogViewProps) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();
  const [resolvedBranchId, setResolvedBranchId] = useState<string | null>(branchId || activeBranchId || null);

  // Auto-resolve branchId if not supplied
  useEffect(() => {
    if (branchId) {
      setResolvedBranchId(branchId);
    } else if (activeBranchId) {
      setResolvedBranchId(activeBranchId);
    } else if (supabase && currentTenantId) {
      supabase
        .from('branches')
        .select('id')
        .eq('tenant_id', currentTenantId)
        .limit(1)
        .maybeSingle()
        .then(({ data }: { data: any }) => {
          if (data?.id) setResolvedBranchId(data.id);
        });
    }
  }, [branchId, activeBranchId, supabase, currentTenantId]);

  // Data states
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [units, setUnits] = useState<UnitItem[]>([]);
  const [hsnSacList, setHsnSacList] = useState<HsnSacItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter & Search states
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Record<string, boolean>>({});

  // Category Modal State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryParentId, setCategoryParentId] = useState<string>('');
  const [categoryIsActive, setCategoryIsActive] = useState(true);
  const [savingCategory, setSavingCategory] = useState(false);

  // Service Item Modal State
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState<ServiceCatalogItem | null>(null);
  const [serviceName, setServiceName] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [serviceCategoryId, setServiceCategoryId] = useState('');
  const [serviceUnitId, setServiceUnitId] = useState('');
  const [serviceBasePrice, setServiceBasePrice] = useState<number | string>(0);
  const [serviceDuration, setServiceDuration] = useState<number | string>(30);
  const [serviceAllowCustomPrice, setServiceAllowCustomPrice] = useState(false);
  const [serviceHsnSacId, setServiceHsnSacId] = useState('');
  const [serviceGstRate, setServiceGstRate] = useState<number | string>(18);
  const [servicePhoto, setServicePhoto] = useState<string | null>(null);
  const [serviceIsActive, setServiceIsActive] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [savingService, setSavingService] = useState(false);

  // Delete Confirmation Modal State
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'category' | 'service';
    id: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Ready-made Services Template Activation Drawer
  const [showTemplateActivation, setShowTemplateActivation] = useState(false);

  // ==========================================
  // Fetch All Master Data
  // ==========================================
  const loadData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Fetch Categories
      const { data: catData, error: catErr } = await supabase
        .from('service_categories')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .order('category_name', { ascending: true });
      if (catErr) throw catErr;

      // 2. Fetch Catalog Items
      const { data: srvData, error: srvErr } = await supabase
        .from('service_catalog_items')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .order('service_name', { ascending: true });
      if (srvErr) throw srvErr;

      // 3. Fetch Units (tenant specific or global)
      const { data: unitsData } = await supabase
        .from('units')
        .select('id, unit_name, unit_symbol, unit_type')
        .or(`tenant_id.is.null,tenant_id.eq.${currentTenantId}`)
        .eq('is_active', true)
        .order('unit_name', { ascending: true });

      // 4. Fetch HSN/SAC codes
      const { data: hsnData } = await supabase
        .from('hsn_sac_master')
        .select('id, code, code_type, description, gst_rate_percent')
        .or(`tenant_id.is.null,tenant_id.eq.${currentTenantId}`)
        .eq('is_active', true)
        .order('code', { ascending: true });

      setCategories(catData || []);
      setServices(srvData || []);
      setUnits(unitsData || []);
      setHsnSacList(hsnData || []);

      // Auto-expand all parent categories by default
      const initialExpanded: Record<string, boolean> = {};
      (catData as ServiceCategory[] || []).forEach((c: ServiceCategory) => {
        if (!c.parent_category_id) initialExpanded[c.id] = true;
      });
      setExpandedCategoryIds(initialExpanded);
    } catch (err: any) {
      console.error('Error loading service catalog data:', err);
      toast.error('Failed to load catalog data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build Hierarchical Category Tree
  const categoryTree = useMemo(() => {
    const parentCategories = categories.filter(c => !c.parent_category_id);
    const subCategoriesMap = new Map<string, ServiceCategory[]>();

    categories.forEach(c => {
      if (c.parent_category_id) {
        const list = subCategoriesMap.get(c.parent_category_id) || [];
        list.push(c);
        subCategoriesMap.set(c.parent_category_id, list);
      }
    });

    return parentCategories.map(parent => ({
      ...parent,
      subCategories: subCategoriesMap.get(parent.id) || [],
    }));
  }, [categories]);

  // Lookup map for fast category name rendering
  const categoryLookup = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach(c => map.set(c.id, c.category_name));
    return map;
  }, [categories]);

  // Lookup map for fast unit symbol/name rendering
  const unitLookup = useMemo(() => {
    const map = new Map<string, string>();
    units.forEach(u => {
      map.set(u.id, u.unit_symbol ? `${u.unit_name} (${u.unit_symbol})` : u.unit_name);
    });
    return map;
  }, [units]);

  // Filtered Services List
  const filteredServices = useMemo(() => {
    return services.filter(item => {
      // Category filter: match item category or match child subcategory
      if (selectedCategoryId) {
        const isDirectMatch = item.category_id === selectedCategoryId;
        const subCatIds = categories
          .filter(c => c.parent_category_id === selectedCategoryId)
          .map(c => c.id);
        const isChildMatch = subCatIds.includes(item.category_id);
        if (!isDirectMatch && !isChildMatch) return false;
      }

      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesName = item.service_name.toLowerCase().includes(q);
        const matchesDesc = (item.description || '').toLowerCase().includes(q);
        const categoryName = (categoryLookup.get(item.category_id) || '').toLowerCase();
        if (!matchesName && !matchesDesc && !categoryName.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [services, selectedCategoryId, searchQuery, categories, categoryLookup]);

  // ==========================================
  // Category Form Handlers
  // ==========================================
  const handleOpenAddCategory = (defaultParentId?: string) => {
    setEditingCategory(null);
    setCategoryName('');
    setCategoryParentId(defaultParentId || '');
    setCategoryIsActive(true);
    setShowCategoryModal(true);
  };

  const handleOpenEditCategory = (cat: ServiceCategory, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCategory(cat);
    setCategoryName(cat.category_name);
    setCategoryParentId(cat.parent_category_id || '');
    setCategoryIsActive(cat.is_active);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;
    if (!categoryName.trim()) {
      toast.error('Please enter category name');
      return;
    }

    setSavingCategory(true);
    try {
      if (editingCategory) {
        const { error } = await supabase
          .from('service_categories')
          .update({
            category_name: categoryName.trim(),
            parent_category_id: categoryParentId || null,
            is_active: categoryIsActive,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingCategory.id);
        if (error) throw error;
        toast.success(`Category "${categoryName}" updated successfully.`);
      } else {
        const { error } = await supabase
          .from('service_categories')
          .insert({
            tenant_id: currentTenantId,
            branch_id: resolvedBranchId || null,
            category_name: categoryName.trim(),
            parent_category_id: categoryParentId || null,
            is_active: categoryIsActive,
          });
        if (error) throw error;
        toast.success(`Category "${categoryName}" created successfully.`);
      }

      setShowCategoryModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Error saving category:', err);
      // Surface exact Postgres error message
      toast.error(err.message || 'Failed to save category');
    } finally {
      setSavingCategory(false);
    }
  };

  // ==========================================
  // Service Catalog Item Form Handlers
  // ==========================================
  const handleOpenAddService = () => {
    setEditingService(null);
    setServiceName('');
    setServiceDescription('');
    setServiceCategoryId(selectedCategoryId || (categories[0]?.id || ''));
    setServiceUnitId(units[0]?.id || '');
    setServiceBasePrice(0);
    setServiceDuration(30);
    setServiceAllowCustomPrice(false);
    setServiceHsnSacId('');
    setServiceGstRate(18);
    setServicePhoto(null);
    setServiceIsActive(true);
    setShowServiceModal(true);
  };

  const handleOpenEditService = (item: ServiceCatalogItem) => {
    setEditingService(item);
    setServiceName(item.service_name);
    setServiceDescription(item.description || '');
    setServiceCategoryId(item.category_id);
    setServiceUnitId(item.unit_id || '');
    setServiceBasePrice(item.base_price ?? 0);
    setServiceDuration(item.duration_minutes ?? 30);
    setServiceAllowCustomPrice(!!item.allow_custom_price);
    setServiceHsnSacId(item.hsn_sac_id || '');
    setServiceGstRate(item.gst_rate ?? 18);
    setServicePhoto(item.photo || null);
    setServiceIsActive(item.is_active);
    setShowServiceModal(true);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !supabase || !currentTenantId) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo size should be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const fileExt = file.name.split('.').pop() || 'jpg';
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9]/g, '_');
      const filePath = `${currentTenantId}/services/${Date.now()}_${cleanFileName}.${fileExt}`;

      const { error: uploadErr } = await supabase.storage
        .from('product-images')
        .upload(filePath, file, { upsert: true });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setServicePhoto(urlData.publicUrl);
      toast.success('Photo uploaded successfully');
    } catch (err: any) {
      console.error('Photo upload error:', err);
      toast.error('Photo upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    if (!serviceName.trim()) {
      toast.error('Please enter service name');
      return;
    }

    if (!serviceCategoryId) {
      toast.error('Please select a category');
      return;
    }

    setSavingService(true);
    try {
      // Ensure branch_id is non-null for service_catalog_items
      let branchToUse = resolvedBranchId;
      if (!branchToUse) {
        const { data: bData } = await supabase
          .from('branches')
          .select('id')
          .eq('tenant_id', currentTenantId)
          .limit(1)
          .maybeSingle();
        branchToUse = bData?.id || null;
      }

      if (!branchToUse) {
        throw new Error('No branch found for this business. Please create or configure a branch first.');
      }

      const payload: any = {
        tenant_id: currentTenantId,
        branch_id: branchToUse,
        category_id: serviceCategoryId,
        service_name: serviceName.trim(),
        description: serviceDescription.trim() || null,
        unit_id: serviceUnitId || null,
        base_price: serviceAllowCustomPrice ? 0 : (Number(serviceBasePrice) || 0),
        duration_minutes: serviceDuration ? Number(serviceDuration) : null,
        allow_custom_price: serviceAllowCustomPrice,
        hsn_sac_id: serviceHsnSacId || null,
        gst_rate: Number(serviceGstRate) || 18,
        photo: servicePhoto || null,
        is_active: serviceIsActive,
        updated_at: new Date().toISOString(),
      };

      if (editingService) {
        const { error } = await supabase
          .from('service_catalog_items')
          .update(payload)
          .eq('id', editingService.id);
        if (error) throw error;
        toast.success(`Service "${serviceName}" updated successfully.`);
      } else {
        const { error } = await supabase
          .from('service_catalog_items')
          .insert(payload);
        if (error) throw error;
        toast.success(`Service "${serviceName}" added to catalog.`);
      }

      setShowServiceModal(false);
      await loadData();
    } catch (err: any) {
      console.error('Error saving service item:', err);
      // Surface exact Postgres error message
      toast.error(err.message || 'Failed to save service');
    } finally {
      setSavingService(false);
    }
  };

  // Toggle active status directly
  const handleToggleServiceActive = async (item: ServiceCatalogItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!supabase) return;
    const targetState = !item.is_active;

    try {
      const { error } = await supabase
        .from('service_catalog_items')
        .update({ is_active: targetState, updated_at: new Date().toISOString() })
        .eq('id', item.id);
      if (error) throw error;

      setServices(prev =>
        prev.map(s => (s.id === item.id ? { ...s, is_active: targetState } : s))
      );
      toast.success(`Service "${item.service_name}" is now ${targetState ? 'ACTIVE' : 'INACTIVE'}.`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update service status');
    }
  };

  // ==========================================
  // Delete Handler with Exact Postgres Error Surfacing
  // ==========================================
  const handleConfirmDelete = async () => {
    if (!supabase || !deleteTarget) return;
    setDeleting(true);

    try {
      if (deleteTarget.type === 'category') {
        const { error } = await supabase
          .from('service_categories')
          .delete()
          .eq('id', deleteTarget.id);

        if (error) {
          // Server-side safety block or foreign key constraint error
          // Explicit requirement: surface the exact Postgres error message returned!
          throw error;
        }

        toast.success(`Category "${deleteTarget.name}" deleted successfully.`);
        if (selectedCategoryId === deleteTarget.id) {
          setSelectedCategoryId(null);
        }
      } else {
        const { error } = await supabase
          .from('service_catalog_items')
          .delete()
          .eq('id', deleteTarget.id);

        if (error) {
          throw error;
        }

        toast.success(`Service "${deleteTarget.name}" deleted successfully.`);
      }

      setDeleteTarget(null);
      await loadData();
    } catch (err: any) {
      console.error('Delete failed:', err);
      // Surface exact Postgres error message
      toast.error(err.message || 'Delete operation failed');
    } finally {
      setDeleting(false);
    }
  };

  const toggleCategoryExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCategoryIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Layers className="text-primary" size={22} />
            Service Catalog & Rate Card
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Organize service categories, customize duration, GST, base prices, or quote-on-demand services.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setShowTemplateActivation(true)}
            variant="outline"
            size="sm"
            className="text-xs flex items-center gap-1.5 border-primary/40 text-primary hover:bg-primary/5 font-semibold"
          >
            <Sparkles size={14} className="text-primary" />
            <span>Ready-made Services List</span>
          </Button>

          <Button
            onClick={() => handleOpenAddCategory()}
            variant="outline"
            size="sm"
            className="text-xs flex items-center gap-1.5 dark:border-slate-800"
          >
            <FolderPlus size={14} className="text-primary" />
            + Add Category
          </Button>

          <Button
            onClick={handleOpenAddService}
            size="sm"
            className="bg-primary hover:bg-primary/90 text-white text-xs flex items-center gap-1.5"
          >
            <Plus size={15} />
            + Add Service
          </Button>
        </div>
      </div>

      {/* Main Grid: Left Categories Panel & Right Services Table */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        {/* ======================================================== */}
        {/* Left Section: Categories Tree                            */}
        {/* ======================================================== */}
        <Card className="lg:col-span-1 border-none shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardHeader className="p-4 pb-3 border-b border-slate-100 dark:border-slate-800 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                <FolderTree size={16} className="text-primary" />
                Categories Tree
              </CardTitle>
              <CardDescription className="text-[11px] text-slate-400">
                Click a category to filter services
              </CardDescription>
            </div>
            <button
              onClick={() => setSelectedCategoryId(null)}
              className={`text-[11px] px-2 py-0.5 rounded font-medium transition-colors ${
                selectedCategoryId === null
                  ? 'bg-primary text-white'
                  : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              All ({services.length})
            </button>
          </CardHeader>

          <CardContent className="p-2 space-y-1">
            {loading ? (
              <div className="py-8 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                <Loader2 size={18} className="animate-spin text-primary" />
                Loading categories...
              </div>
            ) : categories.length === 0 ? (
              <div className="p-4 text-center text-xs text-slate-400 space-y-2">
                <p>No service categories created yet.</p>
                <Button
                  onClick={() => handleOpenAddCategory()}
                  variant="outline"
                  size="sm"
                  className="text-xs h-7 w-full"
                >
                  <Plus size={13} /> Create First Category
                </Button>
              </div>
            ) : (
              <div className="space-y-1 max-h-[600px] overflow-y-auto">
                {categoryTree.map(parent => {
                  const isSelected = selectedCategoryId === parent.id;
                  const isExpanded = !!expandedCategoryIds[parent.id];
                  const hasSub = parent.subCategories.length > 0;
                  const serviceCount = services.filter(s => s.category_id === parent.id).length;

                  return (
                    <div key={parent.id} className="space-y-0.5">
                      {/* Parent Category Row */}
                      <div
                        onClick={() => setSelectedCategoryId(isSelected ? null : parent.id)}
                        className={`group flex items-center justify-between px-2.5 py-2 rounded-lg text-xs cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-primary/10 text-primary font-semibold'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 min-w-0 flex-1">
                          {hasSub ? (
                            <button
                              type="button"
                              onClick={(e) => toggleCategoryExpand(parent.id, e)}
                              className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded text-slate-400"
                            >
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          ) : (
                            <span className="w-4" />
                          )}
                          <span className="truncate">{parent.category_name}</span>
                          {!parent.is_active && (
                            <span className="text-[10px] bg-red-100 dark:bg-red-950/40 text-red-600 px-1 rounded font-normal">
                              Off
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-[10px] text-slate-400 px-1">
                            {serviceCount}
                          </span>
                          <button
                            type="button"
                            title="Add sub-category"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenAddCategory(parent.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:text-primary transition-opacity"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            type="button"
                            title="Edit Category"
                            onClick={(e) => handleOpenEditCategory(parent, e)}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:text-amber-600 transition-opacity"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            title="Delete Category"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget({
                                type: 'category',
                                id: parent.id,
                                name: parent.category_name,
                              });
                            }}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-600 transition-opacity"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      {/* Nested Sub-Categories */}
                      {hasSub && isExpanded && (
                        <div className="pl-6 space-y-0.5 border-l border-slate-200 dark:border-slate-800 ml-3">
                          {parent.subCategories.map(sub => {
                            const isSubSelected = selectedCategoryId === sub.id;
                            const subServiceCount = services.filter(s => s.category_id === sub.id).length;

                            return (
                              <div
                                key={sub.id}
                                onClick={() => setSelectedCategoryId(isSubSelected ? null : sub.id)}
                                className={`group flex items-center justify-between px-2 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
                                  isSubSelected
                                    ? 'bg-primary/10 text-primary font-semibold'
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                                }`}
                              >
                                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                                  <span className="truncate">{sub.category_name}</span>
                                  {!sub.is_active && (
                                    <span className="text-[9px] bg-red-100 dark:bg-red-950/40 text-red-600 px-1 rounded">
                                      Off
                                    </span>
                                  )}
                                </div>

                                <div className="flex items-center gap-1 shrink-0">
                                  <span className="text-[10px] text-slate-400 px-1">
                                    {subServiceCount}
                                  </span>
                                  <button
                                    type="button"
                                    title="Edit Sub-Category"
                                    onClick={(e) => handleOpenEditCategory(sub, e)}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:text-amber-600 transition-opacity"
                                  >
                                    <Pencil size={11} />
                                  </button>
                                  <button
                                    type="button"
                                    title="Delete Sub-Category"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteTarget({
                                        type: 'category',
                                        id: sub.id,
                                        name: sub.category_name,
                                      });
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:text-red-600 transition-opacity"
                                  >
                                    <Trash2 size={11} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ======================================================== */}
        {/* Right Main Section: Services Table                       */}
        {/* ======================================================== */}
        <Card className="lg:col-span-3 border-none shadow-sm dark:bg-slate-900 dark:border-slate-800">
          <CardHeader className="p-4 border-b border-slate-100 dark:border-slate-800 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base font-bold flex items-center gap-2">
                  <span>Services Catalog</span>
                  {selectedCategoryId && (
                    <span className="text-xs font-normal bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                      Filtered: {categoryLookup.get(selectedCategoryId)}
                      <button onClick={() => setSelectedCategoryId(null)} className="hover:text-red-500">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="text-xs text-slate-400">
                  Showing {filteredServices.length} of {services.length} items
                </CardDescription>
              </div>

              {/* Search Bar */}
              <div className="relative w-full sm:w-64">
                <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                <Input
                  placeholder="Search service name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8 text-xs dark:bg-slate-950 dark:border-slate-800"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                <Loader2 size={24} className="animate-spin text-primary" />
                Loading service catalog...
              </div>
            ) : filteredServices.length === 0 ? (
              <div className="py-16 text-center text-slate-400 space-y-3">
                <Layers size={36} className="mx-auto text-slate-300 dark:text-slate-600" />
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {searchQuery || selectedCategoryId ? 'No matching services found.' : 'No services in catalog yet.'}
                </p>
                <p className="text-xs text-slate-400 max-w-sm mx-auto">
                  Click "+ Add Service" to add treatments, repairs, consultations, or bookings to your rate card.
                </p>
                <Button
                  onClick={handleOpenAddService}
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-white text-xs"
                >
                  <Plus size={14} className="mr-1" /> Add First Service
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-100 dark:border-slate-800">
                    <tr>
                      <th className="py-3 px-4">Service Details</th>
                      <th className="py-3 px-3">Category</th>
                      <th className="py-3 px-3">Unit</th>
                      <th className="py-3 px-3">Base Price</th>
                      <th className="py-3 px-3">Duration</th>
                      <th className="py-3 px-3">GST Rate</th>
                      <th className="py-3 px-3 text-center">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                    {filteredServices.map((item) => {
                      const categoryName = categoryLookup.get(item.category_id) || 'Unassigned';
                      const unitLabel = item.unit_id ? unitLookup.get(item.unit_id) || '-' : '-';

                      return (
                        <tr
                          key={item.id}
                          className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors"
                        >
                          {/* Service Name & Photo */}
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {item.photo ? (
                                <img
                                  src={item.photo}
                                  alt={item.service_name}
                                  className="w-10 h-10 rounded-lg object-cover border border-slate-200 dark:border-slate-700 shrink-0"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                  <Layers size={18} />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-semibold text-slate-900 dark:text-white truncate">
                                  {item.service_name}
                                </p>
                                {item.description && (
                                  <p className="text-[11px] text-slate-400 line-clamp-1 max-w-xs">
                                    {item.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Category */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium text-[11px]">
                              {categoryName}
                            </span>
                          </td>

                          {/* Unit */}
                          <td className="py-3 px-3 whitespace-nowrap text-slate-600 dark:text-slate-400 font-medium">
                            {unitLabel}
                          </td>

                          {/* Base Price */}
                          <td className="py-3 px-3 whitespace-nowrap">
                            {Number(item.base_price) === 0 ? (
                              <div className="flex items-center gap-1.5">
                                <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-[10px] font-bold">
                                  Price set karein
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleOpenEditService(item)}
                                  className="text-[11px] text-primary hover:underline font-medium"
                                  title="Edit price"
                                >
                                  Edit ₹
                                </button>
                              </div>
                            ) : item.allow_custom_price ? (
                              <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-300 text-[11px] font-semibold">
                                Quoted / Custom (₹{Number(item.base_price || 0)})
                              </span>
                            ) : (
                              <span className="font-bold text-slate-900 dark:text-slate-100">
                                ₹{Number(item.base_price || 0).toLocaleString('en-IN')}
                              </span>
                            )}
                          </td>

                          {/* Duration */}
                          <td className="py-3 px-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
                            {item.duration_minutes ? (
                              <span className="flex items-center gap-1 font-medium">
                                <Clock size={13} className="text-slate-400" />
                                {item.duration_minutes} mins
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>

                          {/* GST Rate */}
                          <td className="py-3 px-3 whitespace-nowrap text-slate-600 dark:text-slate-400">
                            <span className="font-medium">{item.gst_rate ?? 18}%</span>
                          </td>

                          {/* Status Toggle */}
                          <td className="py-3 px-3 text-center whitespace-nowrap">
                            <button
                              type="button"
                              onClick={(e) => handleToggleServiceActive(item, e)}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition-colors ${
                                item.is_active
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 hover:bg-emerald-200'
                                  : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-300'
                              }`}
                            >
                              {item.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </button>
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleOpenEditService(item)}
                                className="h-7 w-7 p-0 text-slate-500 hover:text-amber-600"
                                title="Edit Service"
                              >
                                <Pencil size={13} />
                              </Button>

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  setDeleteTarget({
                                    type: 'service',
                                    id: item.id,
                                    name: item.service_name,
                                  })
                                }
                                className="h-7 w-7 p-0 text-slate-500 hover:text-red-600"
                                title="Delete Service"
                              >
                                <Trash2 size={13} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ======================================================== */}
      {/* Category Modal (Add / Edit)                              */}
      {/* ======================================================== */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-md overflow-hidden">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <h3 className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <FolderPlus size={16} className="text-primary" />
                {editingCategory ? 'Edit Service Category' : 'Add New Category'}
              </h3>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveCategory} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Category Name *
                </label>
                <Input
                  required
                  placeholder="e.g. Hair Salon, Facial & Spa, AC Repair..."
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  className="dark:bg-slate-950 dark:border-slate-800 text-xs"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Parent Category (Optional)
                </label>
                <select
                  value={categoryParentId}
                  onChange={(e) => setCategoryParentId(e.target.value)}
                  className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-1 text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">None (Top-level Parent Category)</option>
                  {categories
                    .filter(c => !editingCategory || c.id !== editingCategory.id)
                    .filter(c => !c.parent_category_id) // Only allow top-level as parents
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.category_name}
                      </option>
                    ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Leave as None to make this a primary category, or select a parent to create a sub-category.
                </p>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Active in Catalog
                </span>
                <input
                  type="checkbox"
                  checked={categoryIsActive}
                  onChange={(e) => setCategoryIsActive(e.target.checked)}
                  className="rounded accent-primary h-4 w-4"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCategoryModal(false)}
                  disabled={savingCategory}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={savingCategory || !categoryName.trim()}
                  className="bg-primary hover:bg-primary/90 text-white text-xs flex items-center gap-1.5"
                >
                  {savingCategory ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {editingCategory ? 'Update Category' : 'Save Category'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* Service Item Modal (Add / Edit)                          */}
      {/* ======================================================== */}
      {showServiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden my-6">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
              <h3 className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                <Layers size={16} className="text-primary" />
                {editingService ? 'Edit Service Item' : 'Add New Service to Catalog'}
              </h3>
              <button
                onClick={() => setShowServiceModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSaveService} className="p-5 space-y-4 overflow-y-auto flex-1 text-xs">
              {/* Row 1: Name & Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Service Name *
                  </label>
                  <Input
                    required
                    placeholder="e.g. Classic Haircut, Oil Massage..."
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    className="dark:bg-slate-950 dark:border-slate-800 text-xs"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Category *
                  </label>
                  <select
                    required
                    value={serviceCategoryId}
                    onChange={(e) => setServiceCategoryId(e.target.value)}
                    className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-1 text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select Category...</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.parent_category_id ? `↳ ${c.category_name}` : c.category_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Description
                </label>
                <Textarea
                  placeholder="Service process, included items, warranty, terms..."
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  rows={2}
                  className="dark:bg-slate-950 dark:border-slate-800 text-xs"
                />
              </div>

              {/* Row 2: Unit Master Picker & Duration */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Unit Master (Billing Unit)
                  </label>
                  <select
                    value={serviceUnitId}
                    onChange={(e) => setServiceUnitId(e.target.value)}
                    className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-1 text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select Unit (e.g. Session, Hour, Piece)...</option>
                    {units.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.unit_name} {u.unit_symbol ? `(${u.unit_symbol})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1">
                    <Clock size={12} className="text-slate-400" />
                    Duration (Minutes)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step={5}
                    placeholder="e.g. 30, 45, 60..."
                    value={serviceDuration}
                    onChange={(e) => setServiceDuration(e.target.value)}
                    className="dark:bg-slate-950 dark:border-slate-800 text-xs"
                  />
                </div>
              </div>

              {/* Custom / Quoted Price Toggle & Base Price */}
              <div className="p-3.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-800 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={serviceAllowCustomPrice}
                    onChange={(e) => setServiceAllowCustomPrice(e.target.checked)}
                    className="rounded accent-primary h-4 w-4"
                  />
                  <span>Allow custom / quoted price (Manual quote at billing time)</span>
                </label>

                {serviceAllowCustomPrice ? (
                  <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 p-2 rounded border border-amber-200 dark:border-amber-900/40">
                    💡 Base Price is disabled because this service will be quoted dynamically when the professional bills the client.
                  </p>
                ) : (
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1">
                      <IndianRupee size={12} className="text-slate-400" />
                      Base Price (₹) *
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      value={serviceBasePrice}
                      onChange={(e) => setServiceBasePrice(e.target.value)}
                      className="dark:bg-slate-900 dark:border-slate-800 text-xs font-bold"
                    />
                  </div>
                )}
              </div>

              {/* Row 3: HSN/SAC Code & GST Rate % */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    HSN / SAC Code
                  </label>
                  <select
                    value={serviceHsnSacId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setServiceHsnSacId(val);
                      const found = hsnSacList.find(h => h.id === val);
                      if (found && found.gst_rate_percent !== null && found.gst_rate_percent !== undefined) {
                        setServiceGstRate(found.gst_rate_percent);
                      }
                    }}
                    className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-1 text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="">Select HSN/SAC Master...</option>
                    {hsnSacList.map(h => (
                      <option key={h.id} value={h.id}>
                        {h.code} ({h.code_type || 'SAC'}) - {h.description || 'Service'} ({h.gst_rate_percent || 0}%)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1 flex items-center gap-1">
                    <Percent size={12} className="text-slate-400" />
                    GST Rate % (Default 18%)
                  </label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    placeholder="18"
                    value={serviceGstRate}
                    onChange={(e) => setServiceGstRate(e.target.value)}
                    className="dark:bg-slate-950 dark:border-slate-800 text-xs"
                  />
                </div>
              </div>

              {/* Photo Upload Component */}
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Service Photo / Artwork
                </label>
                <div className="flex items-center gap-4">
                  {servicePhoto ? (
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shrink-0">
                      <img
                        src={servicePhoto}
                        alt="Service Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setServicePhoto(null)}
                        className="absolute top-1 right-1 p-0.5 bg-red-600 text-white rounded-full hover:bg-red-700"
                        title="Remove photo"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-slate-100 dark:bg-slate-800 border border-dashed border-slate-300 dark:border-slate-700 flex flex-col items-center justify-center text-slate-400 shrink-0">
                      <ImageIcon size={20} />
                      <span className="text-[9px]">No photo</span>
                    </div>
                  )}

                  <div className="flex-1 space-y-1">
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                      <Upload size={13} />
                      <span>{uploadingPhoto ? 'Uploading...' : servicePhoto ? 'Change Photo' : 'Upload Photo'}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        className="hidden"
                      />
                    </label>
                    <p className="text-[10px] text-slate-400">
                      Supports JPG, PNG, WebP (Max 5MB). Stored in bucket.
                    </p>
                  </div>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
                <span className="font-medium text-slate-700 dark:text-slate-300">
                  Active in Rate Card & Appointments
                </span>
                <input
                  type="checkbox"
                  checked={serviceIsActive}
                  onChange={(e) => setServiceIsActive(e.target.checked)}
                  className="rounded accent-primary h-4 w-4"
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowServiceModal(false)}
                  disabled={savingService}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={savingService || !serviceName.trim() || !serviceCategoryId}
                  className="bg-primary hover:bg-primary/90 text-white text-xs flex items-center gap-1.5"
                >
                  {savingService ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  {editingService ? 'Update Service' : 'Add to Catalog'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* Delete Confirmation Modal (Surfacing Postgres Errors)    */}
      {/* ======================================================== */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-sm p-5 space-y-4">
            <div className="flex items-center gap-3 text-red-600">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-950/50">
                <AlertCircle size={22} />
              </div>
              <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                Delete {deleteTarget.type === 'category' ? 'Category' : 'Service'}?
              </h3>
            </div>

            <p className="text-xs text-slate-600 dark:text-slate-300">
              Are you sure you want to delete{' '}
              <strong className="text-slate-900 dark:text-white">"{deleteTarget.name}"</strong>?
              {deleteTarget.type === 'category' && (
                <span className="block mt-1 text-slate-500">
                  Note: Categories with existing catalog services or sub-categories cannot be deleted.
                </span>
              )}
            </p>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="text-xs flex items-center gap-1.5"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Confirm Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Ready-made Services Activation Drawer */}
      {showTemplateActivation && currentTenantId && (
        <ServiceTemplateActivationDrawer
          tenantId={currentTenantId}
          branchId={resolvedBranchId || ''}
          isOpen={showTemplateActivation}
          onClose={() => setShowTemplateActivation(false)}
          onActivated={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
}
